/**
 * =============================================================================
 * navDebugLogger.ts
 * =============================================================================
 *
 * Logger leggero e in-memory per tracciare gli eventi di navigazione critici:
 * - Redirect verso /auth
 * - Salvataggi/ripristini di scroll e route intese
 * - Eventi visibilitychange / pagehide / popstate
 *
 * Gli eventi sono visualizzabili tramite il NavDebugPanel quando l'utente
 * abilita il debug aggiungendo `?debug=nav` all'URL oppure impostando
 * `localStorage["lni:nav-debug"] = "1"`.
 *
 * Il logger è interamente opt-in: in produzione, senza flag attivo, non
 * trattiene eventi e non scrive in console, mantenendo performance e privacy.
 * =============================================================================
 */

/** Livelli supportati per la categorizzazione visiva nel pannello debug. */
export type NavDebugLevel = "info" | "warn" | "error";

/** Singolo evento tracciato. */
export interface NavDebugEvent {
  /** Timestamp epoch ms. */
  ts: number;
  /** Categoria libera (es: "auth", "scroll", "popstate", "visibility"). */
  source: string;
  /** Messaggio breve descrittivo. */
  message: string;
  /** Dati extra opzionali per ispezione. */
  data?: Record<string, unknown>;
  /** Livello informativo. */
  level: NavDebugLevel;
}

/** Chiave localStorage per attivare il debug in modo persistente. */
const STORAGE_FLAG_KEY = "lni:nav-debug";

/** Dimensione massima del buffer in memoria. */
const MAX_EVENTS = 200;

/** Buffer in memoria condiviso tra logger e pannello. */
const eventBuffer: NavDebugEvent[] = [];

/** Subscriber che vengono notificati ad ogni nuovo evento. */
const subscribers = new Set<(events: NavDebugEvent[]) => void>();

/**
 * Verifica se il debug è attivo: query string `?debug=nav` oppure flag
 * persistente in localStorage. Tollerante ad ambienti senza window.
 */
export function isNavDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("debug") === "nav") {
      // Persistiamo il flag per sopravvivere ai reload successivi.
      window.localStorage.setItem(STORAGE_FLAG_KEY, "1");
      return true;
    }
    return window.localStorage.getItem(STORAGE_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/** Attiva manualmente il debug (usato dal pannello). */
export function enableNavDebug(): void {
  try {
    window.localStorage.setItem(STORAGE_FLAG_KEY, "1");
  } catch {
    /* noop */
  }
}

/** Disattiva il debug e svuota il buffer. */
export function disableNavDebug(): void {
  try {
    window.localStorage.removeItem(STORAGE_FLAG_KEY);
  } catch {
    /* noop */
  }
  clearNavDebugEvents();
}

/** Aggiunge un evento al buffer (solo se il debug è attivo). */
export function logNavDebug(
  source: string,
  message: string,
  data?: Record<string, unknown>,
  level: NavDebugLevel = "info",
): void {
  if (!isNavDebugEnabled()) return;

  const event: NavDebugEvent = {
    ts: Date.now(),
    source,
    message,
    data,
    level,
  };

  eventBuffer.push(event);
  if (eventBuffer.length > MAX_EVENTS) {
    // Manteniamo solo gli ultimi MAX_EVENTS per limitare memoria/render.
    eventBuffer.splice(0, eventBuffer.length - MAX_EVENTS);
  }

  // Notifica reattiva al pannello debug.
  for (const subscriber of subscribers) {
    try {
      subscriber([...eventBuffer]);
    } catch {
      /* ignoriamo errori dei subscriber per non rompere la navigazione */
    }
  }

  // Log in console solo in dev mode o quando il flag è attivo, prefisso chiaro.
  // eslint-disable-next-line no-console
  const consoleFn = level === "error" ? console.error : level === "warn" ? console.warn : console.info;
  consoleFn(`[nav-debug:${source}] ${message}`, data ?? "");
}

/** Restituisce lo snapshot corrente degli eventi. */
export function getNavDebugEvents(): NavDebugEvent[] {
  return [...eventBuffer];
}

/** Svuota il buffer e notifica i subscriber. */
export function clearNavDebugEvents(): void {
  eventBuffer.length = 0;
  for (const subscriber of subscribers) {
    try {
      subscriber([]);
    } catch {
      /* noop */
    }
  }
}

/** Sottoscrive un subscriber e restituisce la funzione di unsubscribe. */
export function subscribeNavDebug(
  subscriber: (events: NavDebugEvent[]) => void,
): () => void {
  subscribers.add(subscriber);
  // Fornisce subito lo stato corrente.
  subscriber([...eventBuffer]);
  return () => {
    subscribers.delete(subscriber);
  };
}